// <copyright file="header.ts" company="BD">
// Copyright (c) BD. All rights reserved.
// Created: 10/09/2025 by Naziya Firdos
// </copyright>

import { Component, inject } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatMenuModule } from "@angular/material/menu";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import { AuthService } from "../../../features/authenticate/auth-service";
import { CommonAPiService } from "../../services/common-service";
import { ConnectedPosition, OverlayModule } from "@angular/cdk/overlay";
import { CommonConfirmDialogComponent } from "../common-confirm-dialog/common-confirm-dialog";
import { ConfirmDialogAdditionalData } from "../common-confirm-dialog/common-confirm-dialog.model";
import { SessionTimeoutService } from "../../services/session-timeout-service";

@Component({
  selector: "app-header",
  imports: [MatMenuModule, MatButtonModule, TranslatePipe, OverlayModule],
  templateUrl: "./header.html",
  styleUrl: "./header.scss"
})
// Defines Header.
export class Header {
  private readonly commonApiService = inject(CommonAPiService);
  users = this.commonApiService.users;
  readonly webAppInfo = this.commonApiService.webAppVersion;
  private readonly router = inject(Router);
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private sessionTimeoutService = inject(SessionTimeoutService);

  // Popup state management
  public isSettingsPopupOpen = false;
  public isUserPopupOpen = false;
  public currentPositions: ConnectedPosition[] = [];
  public userPopupPositions: ConnectedPosition[] = [];

  onSignOut(): void {
    this.closeUserPopup();
    this.sessionTimeoutService.stopWatching();
    this.authService.logout();
  }

  navigateToLandingPage(): void {
    this.router.navigate(["/mobileupgradeportal"]);
  }

  /**
   * Calculate and return the correct positions to open the popup below the settings icon
   * with the caret on the right side of the popup
   * @returns Array of ConnectedPosition for the CDK overlay
   */
  getPopUpPositions(): ConnectedPosition[] {
    const positions: ConnectedPosition[] = [
      {
        originX: "center",
        originY: "bottom",
        overlayX: "end",
        overlayY: "top",
        offsetY: 8,
        offsetX: 12
      }
    ];
    return positions;
  }

  /**
   * Calculate and return the correct positions to open the user popup below the user icon
   * @returns Array of ConnectedPosition for the CDK overlay
   */
  getUserPopUpPositions(): ConnectedPosition[] {
    const positions: ConnectedPosition[] = [
      {
        originX: "center",
        originY: "bottom",
        overlayX: "end",
        overlayY: "top",
        offsetY: 7,
        offsetX: 22
      }
    ];
    return positions;
  }

  public prepareAboutDialogData(): ConfirmDialogAdditionalData {
    const info = this.webAppInfo();

    return {
      title: "HEADER.ABOUT_DIALOG.TITLE",
      showAboutInfo: true,
      showFooter: false,
      aboutVersionLabel: "HEADER.ABOUT_DIALOG.VERSION_LABEL",
      aboutVersion: info?.webappVersion,
      aboutUpdatedLabel: "HEADER.ABOUT_DIALOG.UPDATED_LABEL",
      aboutUpdatedDate: this.formatBuildDate(info?.buildUpdated),
      aboutSupportLabel: "HEADER.ABOUT_DIALOG.SUPPORT_LABEL",
      aboutSupportEmail: info?.emailSupport
    };
  }

  toggleSettingsPopup(): void {
    if (this.isSettingsPopupOpen) {
      this.closeSettingsPopup();
    } else {
      this.closeUserPopup();
      this.currentPositions = this.getPopUpPositions();
      this.isSettingsPopupOpen = true;
    }
  }

  closeSettingsPopup(): void {
    this.isSettingsPopupOpen = false;
  }

  toggleUserPopup(): void {
    if (this.isUserPopupOpen) {
      this.closeUserPopup();
    } else {
      this.closeSettingsPopup();
      this.userPopupPositions = this.getUserPopUpPositions();
      this.isUserPopupOpen = true;
    }
  }

  closeUserPopup(): void {
    this.isUserPopupOpen = false;
  }

  onAboutClick(): void {
    this.closeSettingsPopup();
    this.openAboutDialog();
  }

  /**
   * Open the About dialog with version and support information
   */
  openAboutDialog(): void {
    this.dialog.open(CommonConfirmDialogComponent, {
      width: "420px",
      height: "320px",
      maxWidth: "none",
      data: this.prepareAboutDialogData(),
      disableClose: false
    });
  }

  /**
   * Converts an ISO UTC datetime string to localized date in MM/DD/YYYY format.
   * Expected input format: "YYYY-MM-DDTHH:mm:ssZ"
   * @param value - UTC datetime string
   * @returns Localized date in MM/DD/YYYY format, original value for invalid input, or undefined for empty input
   */
  private formatBuildDate(value: string | undefined): string | undefined {
    const normalizedValue = value?.trim();

    if (!normalizedValue) return undefined;

    const parsedDate = new Date(normalizedValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return value;
    }
    return parsedDate.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric"
    });
  }
}
